import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";

const commandMap: { patterns: RegExp[]; action: string; path?: string; message?: string }[] = [
  {
    patterns: [/rumors/i, /ultim[ie] notizie/i, /news/i, /notizie/i],
    action: "navigate",
    path: "/news",
    message: "Vado alla sezione News",
  },
  {
    patterns: [/messaggi/i, /chat/i, /messaggio/i],
    action: "navigate",
    path: "/messaggi",
    message: "Apro i messaggi",
  },
  {
    patterns: [/prodott/i, /iphone/i, /shop/i, /acquist/i, /comprar/i],
    action: "navigate",
    path: "/prodotti",
    message: "Vado ai prodotti",
  },
  {
    patterns: [/sfond/i, /wallpaper/i],
    action: "navigate",
    path: "/sfondi",
    message: "Apro gli sfondi",
  },
  {
    patterns: [/confronta/i, /compara/i, /differenz/i],
    action: "navigate",
    path: "/confronta-iphone",
    message: "Apro il confronto iPhone",
  },
  {
    patterns: [/profilo/i, /account/i, /impostazioni/i],
    action: "navigate",
    path: "/profile",
    message: "Apro il profilo",
  },
  {
    patterns: [/bacheca/i, /post/i, /social/i],
    action: "navigate",
    path: "/bacheca",
    message: "Apro la bacheca",
  },
  {
    patterns: [/home/i, /casa/i, /inizio/i],
    action: "navigate",
    path: "/",
    message: "Torno alla home",
  },
];

export interface VoiceIntent {
  type: "navigate" | "question";
  message?: string;
  path?: string;
}

export function useVoiceCommands() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef<any>(null);

  const startListening = useCallback(() => {
    // Always create a fresh instance
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Il riconoscimento vocale non è supportato dal tuo browser");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "it-IT";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 3;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript("");
    };

    recognition.onresult = (event: any) => {
      const nextTranscript = Array.from(event.results)
        .map((result: any) => result[0]?.transcript ?? "")
        .join(" ")
        .trim();

      setTranscript(nextTranscript);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event: any) => {
      if (event.error !== "aborted") {
        toast.error("Errore nel riconoscimento vocale");
      }
      setIsListening(false);
      recognitionRef.current = null;
    };

    try {
      recognition.start();
    } catch {
      setIsListening(false);
    }
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, []);

  const clearVoiceState = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    setIsListening(false);
    setTranscript("");
  }, []);

  const processCommand = useCallback(
    (text: string): VoiceIntent => {
      const lower = text.trim().toLowerCase();

      if (!lower) {
        return { type: "question" };
      }

      for (const cmd of commandMap) {
        for (const pattern of cmd.patterns) {
          if (pattern.test(lower)) {
            if (cmd.action === "navigate" && cmd.path) {
              return {
                type: "navigate",
                message: cmd.message || `Navigo a ${cmd.path}`,
                path: cmd.path,
              };
            }
          }
        }
      }
      return { type: "question" };
    },
    []
  );

  return { isListening, transcript, startListening, stopListening, clearVoiceState, processCommand };
}
